<template>
  <main>
    <ErrorModal v-if="isErrorModalOpened" :content="global.errorContent"/>
    <div v-if="busy" class="h-screen">
      <KLoading/>
    </div>
    <template v-else>
      <div class="mb-12">
        <router-view />
      </div>
      <KDisclaimer :disclaimerText="global.footerDisclaimer" :content="global.disclaimerModalContent"/>
    </template>
  </main>
</template>

<script lang="ts">
import { onMounted, onUnmounted, computed, defineComponent, watch } from 'vue'
import { useFlow, useGlobal } from '@/store'
import { ErrorModal, KLoading, KDisclaimer } from '@/components'
import { useRoute } from 'vue-router'
import { adjustLightness } from './utils/colors'
import { storeToRefs } from 'pinia'
import { getKbygDecryptUrl } from '@/api/get-kbgy-decrypt-url'
import { KbygParams } from '@/model'

export default defineComponent({
  components: {
    ErrorModal,
    KLoading,
    KDisclaimer
  },
  setup () {
    const global = useGlobal()
    const flow = useFlow()
    const route = useRoute()

    const busy = computed(() => global.busy || flow.busy)
    const { isErrorModalOpened } = storeToRefs(global)

    const decryptUrlParams = async (encryptedPayload: string) => {
      return encryptedPayload ? await getKbygDecryptUrl(encryptedPayload) : null
    };

    const parseQueryString = (queryString: string) => {
      const params = new URLSearchParams(queryString);
      const result: { [key: string]: string } = {};
      params.forEach((value, key) => {
        result[key] = value;
      });
      return result;
    };

    const getQueryParams  = async (query:any) :  Promise<KbygParams> => {
      if(query.search){
        const encryptedParam = (query.search as string)?.replace(/ /g, '+');
        const decryptedParams = await decryptUrlParams(encryptedParam);
        const parsedDecryptedParams = parseQueryString(decryptedParams as string); 
        return KbygParams.of(parsedDecryptedParams);
      }    
      return KbygParams.of(query);
    };

    const sendHeightToParent = () => {
      const height = document.body.scrollHeight
      window.parent.postMessage({ type: 'bpp-resize', height }, '*')
    }

    let resizeObserver: ResizeObserver | null = null

    onMounted(() => {
      sendHeightToParent()
      resizeObserver = new ResizeObserver(() => sendHeightToParent())
      resizeObserver.observe(document.body)
    })

    onUnmounted(() => {
      resizeObserver?.disconnect()
    })

    watch(
      [() => route.query, () => route.params],
      async ([query, params]) => {
        const root = document.documentElement
        if (params.locale) {
          root.setAttribute('lang', params.locale as string)
        } else {
          return
        }
        if (global.clientId) {
          return
        }
        
        const kbygParams = await getQueryParams(query);
        const { groupCk, groupId, classId, classPlanId, showEye360, packageId, clientId } = kbygParams;
          
        if (!groupCk || !groupId || !groupId || !classPlanId) {
            return
        }

        global.$patch({
          groupCk: groupCk as string,
          groupId: groupId as string,
          classId: classId as string,
          classPlanId: classPlanId as string,
          showEye360: showEye360 === 'true',
          packageId: packageId as string || '',
          encryptedURL:query.search ? query.search as string : ''
        })
    
        await global.getGlobalConfiguration(
          clientId as string,
          params.locale as string
        )
        await global.getHomepageData(
          clientId as string,
          global.locale
        )
        await flow.getFlow(
          global.bppFlowIdList,
          global.locale
        )

        // Set color palette
        root.style.setProperty('--color-primary', global.primaryColor)
        root.style.setProperty('--color-primary-dark', global.tertiaryColor)
        root.style.setProperty('--color-primary-light', global.secondaryColor)
        root.style.setProperty('--color-primary-hover', adjustLightness(global.primaryColor, 15))
        root.style.setProperty('--color-secondary-hover', adjustLightness(global.secondaryColor, 10))
      }
    )

    return { global, busy, isErrorModalOpened }
  }
})
</script>
